// ABOUTME: Toast notification context provider for managing stackable toast messages
// ABOUTME: Provides toast API for success, error, warning, and info notifications with auto-dismiss

import { Alert, AlertColor } from "@mui/material";
import React, { useState } from "react";
import {
  Toast,
  ToastContext,
  ToastContextType,
} from "./ToastContextDefinition";

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (
    message: string,
    severity: AlertColor = "info",
    options: Partial<Toast> = {},
  ) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      message,
      severity,
      autoHideDuration: 6000,
      ...options,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto dismiss if autoHideDuration is set
    if (newToast.autoHideDuration && newToast.autoHideDuration > 0) {
      setTimeout(() => {
        // Check if toast still exists before dismissing
        setToasts((currentToasts) => {
          const toastExists = currentToasts.find((t) => t.id === id);
          if (toastExists) {
            return currentToasts.filter((t) => t.id !== id);
          }
          return currentToasts;
        });
      }, newToast.autoHideDuration);
    }
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showSuccess = (message: string, options: Partial<Toast> = {}) => {
    showToast(message, "success", options);
  };

  const showError = (message: string, options: Partial<Toast> = {}) => {
    showToast(message, "error", { autoHideDuration: 8000, ...options });
  };

  const showWarning = (message: string, options: Partial<Toast> = {}) => {
    showToast(message, "warning", options);
  };

  const showInfo = (message: string, options: Partial<Toast> = {}) => {
    showToast(message, "info", options);
  };

  const showPersistent = (
    message: string,
    severity: AlertColor = "info",
    action?: React.ReactNode,
  ) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      message,
      severity,
      autoHideDuration: 0, // Never auto-dismiss
      action,
    };

    setToasts((prev) => [...prev, newToast]);
    return id; // Return ID so caller can dismiss it later
  };

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPersistent,
    dismissToast,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Custom toast container to handle proper stacking */}
      {toasts.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "16px",
            right: "16px",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column-reverse", // Stack from bottom up
            gap: "8px",
            pointerEvents: "none", // Allow clicks through container
          }}
        >
          {toasts.map((toast) => (
            <div key={toast.id} style={{ pointerEvents: "auto" }}>
              <Alert
                onClose={() => dismissToast(toast.id)}
                severity={toast.severity}
                variant="filled"
                action={toast.action}
                sx={{
                  width: "300px",
                  boxShadow: 3,
                  "& .MuiAlert-action": {
                    alignItems: "center",
                  },
                }}
              >
                {toast.message}
              </Alert>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};
